import { Box, Checkbox } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { stepModeEnum } from '../../constants';
import {
    useCreateDailySubContractorMutation,
    useDailySubContractorMutation,
    useDeleteDailySubContractorMutation,
} from '../../hooks/mutations/useDailySubContractorMutation';
import { useDailyResource } from '../../hooks/useDailyResource';
import { DataItem } from '../../services/apiSLTP/dailyReports/activities';
import { DailySubContractor } from '../../services/apiSLTP/dailyReports/dailySubContractors';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import DraggableCell from '../dnd/DraggableCell';
import DroppableCell from '../dnd/DroppableCell';
import ManageDailySubContractorModal from '../modals/operations/planning/ManageDailySubContractorModal';
import IconButton from '../ui/IconButton';
import { DND_ITEM_TYPES } from '../../constants/dnd';

interface SubContractorsCellProps {
    activity: DataItem;
    activities: DataItem[];
    activityIndex: number;
    disabled?: boolean;
    hiddenPlusIcon?: boolean;
    refetch?: () => void;
}

const SubContractorsCell: React.FC<SubContractorsCellProps> = ({
    activity,
    activityIndex,
    activities,
    disabled = false,
    hiddenPlusIcon = false,
    refetch = () => {},
}) => {
    const { t } = useTranslation();
    const itemType = DND_ITEM_TYPES.SUBCONTRACTORS;

    const { mutate: updateDailySubContractor } = useDailySubContractorMutation({ onSuccess: refetch });
    const { mutate: deleteDailySubContractor } = useDeleteDailySubContractorMutation({ onSuccess: refetch });
    const { mutate: createDailySubContractor } = useCreateDailySubContractorMutation({ onSuccess: refetch });

    const { selectedItem, manageModal, handleMove, handleDelete, handleDuplicate, openModal } = useDailyResource<DailySubContractor>({
        onMove: async (fromLineId, itemId, toLineId) => {
            updateDailySubContractor({ id: itemId, body: { activityId: toLineId } });
        },
        onDelete: async (itemId) => {
            deleteDailySubContractor({ id: itemId });
        },
        onDuplicate: async (subContractor) => {
            createDailySubContractor({
                body: {
                    activityId: activity._id,
                    subContractorTypeId: subContractor.subContractorTypeId?._id,
                    price: subContractor.price,
                    priceType: subContractor.priceType,
                },
            });
        },
    });

    const showCheckbox = activity.stepMode === stepModeEnum.VALIDATE;

    return (
        <Box sx={{ display: 'flex', height: '100%', width: '100%', flex: 1, opacity: disabled ? 0.7 : 1, color: 'black' }}>
            <DroppableCell dropId={{ lineId: activity._id }} moveCell={handleMove} itemType={itemType} disabled={disabled}>
                <div className="fx-r pos-re fx-fg-1 gap-5px">
                    <div>
                        {!hiddenPlusIcon && (
                            <IconButton
                                iconName="addCircle"
                                onClick={() => openModal(null)}
                                disabled={disabled}
                                title={capitalizeFirstLetter(t('addSubContractor'))}
                            />
                        )}
                    </div>
                    <div className="gap-5px fx-c fx-fg-1">
                        {activity.dailySubContractors.map((subContractor, index) => (
                            <div key={`${activityIndex}-${index}`} className="fx-r fx-fg-1 gap-5px fx-items-fs">
                                <DraggableCell
                                    dragIds={{ itemId: subContractor._id, lineId: activity._id }}
                                    index={index}
                                    itemType={itemType}
                                    onDelete={() => handleDelete(subContractor._id)}
                                    disabled={disabled}
                                    state={subContractor.state}
                                    onDuplicate={() => handleDuplicate(subContractor)}
                                    onDoubleClick={() => openModal(subContractor)}
                                >
                                    <div title={subContractor.subContractorTypeId?.designation} className="one-line-container">
                                        <div className="font-w-700 one-line">{subContractor.subContractorTypeId?.designation}</div>
                                        {activity.stepMode === stepModeEnum.VALIDATE && (
                                            <div className="one-line" title={subContractor.subContractorTypeId?.codeProC}>
                                                {subContractor.subContractorTypeId?.codeProC}
                                            </div>
                                        )}
                                    </div>
                                </DraggableCell>

                                {showCheckbox && (
                                    <div className="h-25px fx-r fx-items-c" title={capitalizeFirstLetter(t('includedInExpenses'))}>
                                        <Checkbox
                                            checked={subContractor.includedInExpenses}
                                            onChange={(_, checked) => {
                                                updateDailySubContractor(
                                                    { id: subContractor._id, body: { includedInExpenses: checked } },
                                                    {
                                                        onSuccess: () => {
                                                            refetch();
                                                        },
                                                    }
                                                );
                                            }}
                                            size="small"
                                            color="success"
                                            sx={{ padding: 0 }}
                                            disabled={disabled || !activity.includedInExpenses}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </DroppableCell>

            {manageModal.value && (
                <ManageDailySubContractorModal
                    open={manageModal.value}
                    activity={activity}
                    activityIndex={activityIndex}
                    handleClose={() => manageModal.setFalse()}
                    refetch={refetch}
                    dailySubContractor={selectedItem}
                />
            )}
        </Box>
    );
};

export default SubContractorsCell;
